import moment from 'moment';
import { isEmailValid } from '@gjensidige/se-helpers';
import { InsuranceHelper } from './InsuranceHelper';
import {
  AREA_LIMITS,
  LIVING_AREA_MAX,
  MC_INVALID_PARKING,
  MIN_CONSTRUCTION_YEAR_VILLA
} from '../lib/constants/appConstants';
import { IPricingStepConfig } from '../interfaces/EnquiryInterface';
import { PricingStepConfig } from '../interfaces/Types';
import { AppConstants } from './AppConstants';
import { TextHelper } from './TextHelper';

export class Validator {
  static validate = (
    category: string,
    value: any,
    productName: string,
    pricingQuestions?: PricingStepConfig[],
    dependentValue?: string
  ) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    switch (category) {
      case 'area':
        status = Validator.areaValidator(value, productName);
        break;
      case 'noOfPeople':
        status = Validator.numberOfPeopleValidator(parseInt(value), productName);
        break;
      case 'builtYear':
        status = Validator.builtYearValidator(parseInt(value), productName);
        break;
      case 'inspectionYear':
        status = Validator.inspectionYearValidator(
          parseInt(value),
          productName,
          pricingQuestions,
          dependentValue
        );
        break;
      case 'roofConstruction':
        status = Validator.livingAreaValidator(parseInt(value));
        break;
      case 'extraBuildingArea':
        status = Validator.extraBuildinglivingAreaValidator(parseInt(value));
        break;
      case 'noOfFloors':
        status = Validator.numberOfWetRoomsValidator(parseInt(value));
        break;
      case 'email':
        status = Validator.emailValidator(value);
        break;
      case 'mcType':
        status = Validator.mcTypeValidator(value);
    }
    return status;
  };

  static numberOfPeopleValidator = (value: number, productName: string) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    if (value > 6) {
      const productChannelKey = `${productName}-${InsuranceHelper.getChannelInfo()?.toLowerCase()}`;
      status = {
        isValid: false,
        errorMessage: TextHelper.getNumberOfPeopleValidationMessage(productChannelKey)
      };
    }
    return status;
  };
  static emailValidator = (value: string) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    const errorMessages = {
      EMAIL_REQUIRED: AppConstants.getAppText(
        'webshop.summary.save.emailFieldRequired'
      ),
      EMAIL_INVALID: AppConstants.getAppText(
        'webshop.summary.save.emailFieldInvalid'
      )
    };
    const emailValidationMessage = isEmailValid(value, errorMessages);
    if (emailValidationMessage) {
      status = {
        isValid: false,
        errorMessage: emailValidationMessage
      };
    }
    return status;
  };

  static mcTypeValidator = (value: string) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    if (value === MC_INVALID_PARKING.STREET_PARKING) {
      status = {
        ...status,
        isValid: false,
      };
    }
    return status;
  };

  static numberOfWetRoomsValidator = (value: number) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    if (value > 3) {
      status = {
        isValid: false,
        errorMessage: AppConstants.getAppText(
          'webshop.villa.noOfWetRoomsExceedsLimitText'
        )
      };
    }
    return status;
  };

  static livingAreaValidator = (value: number) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    if (value > LIVING_AREA_MAX.MAIN_BUILDING) {
      status = {
        isValid: false,
        errorMessage: AppConstants.getAppText(
          'webshop.villa.livingAreaExceedsLimitText'
        )
      };
    }
    return status;
  };
  static extraBuildinglivingAreaValidator = (value: number) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    if (value > LIVING_AREA_MAX.EXTRA_BUILDING) {
      status = {
        isValid: false,
        errorMessage: AppConstants.getAppText(
          'webshop.villa.additionalBuilding.areaExceedsLimitText'
        )
      };
    }
    return status;
  };

  static areaValidator = (value: any, productName: string) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    const productChannelKey = `${productName}-${InsuranceHelper.getChannelInfo()?.toLowerCase()}`;
    if (!value) {
      status = {
        isValid: false,
        errorMessage: 'Obligatoriskt fält.'
      };
    } else if (value > AREA_LIMITS[productChannelKey].MAX) {
      status = {
        isValid: false,
        errorMessage: TextHelper.getAreaValidationMessage(productChannelKey, false)
      };
    } else if (value < AREA_LIMITS[productChannelKey].MIN) {
      status = {
        isValid: false,
        errorMessage: TextHelper.getAreaValidationMessage(productChannelKey, true)
      };
    }
    return status;
  };

  static builtYearValidator = (value: any, productName: string) => {
    let status = {
      isValid: true,
      errorMessage: ''
    };
    if (!value) {
      status = {
        isValid: false,
        errorMessage: ''
      };
    } else if (value > new Date().getFullYear()) {
      status = {
        isValid: false,
        errorMessage: TextHelper.getBuiltYearValidationMessage(
          productName,
          false
        )
      };
    } else if (value < MIN_CONSTRUCTION_YEAR_VILLA) {
      status = {
        isValid: false,
        errorMessage: TextHelper.getBuiltYearValidationMessage(
          productName,
          true,
          MIN_CONSTRUCTION_YEAR_VILLA
        )
      };
    }

    return status;
  };

  static inspectionYearValidator = (
    value: any,
    productName: string,
    pricingQuestions?: PricingStepConfig[],
    dependentValue?: string
  ) => {
    const stepQuestions = pricingQuestions?.find(
      (item: IPricingStepConfig) => item.stepCode === '001'
    );
    const builtYear = dependentValue
      ? dependentValue
      : stepQuestions?.questions.find(
          (question) => question.APIKey === 'builtYear'
        )?.selectedValue || '';
    let status = {
      isValid: true,
      errorMessage: ''
    };
    if (!value) {
      status = {
        isValid: false,
        errorMessage: ''
      };
    } else if (value && builtYear && parseInt(builtYear) > value) {
      status = {
        isValid: false,
        errorMessage: AppConstants.getAppText(
          'webshop.villa.inspectionYearGreaterThanBuiltYear'
        )
      };
    }
    if (value > new Date().getFullYear()) {
      status = {
        isValid: false,
        errorMessage: AppConstants.getAppText(
          'webshop.villa.inspectionYearGreaterThanCurrentYear'
        )
      };
    }
    return status;
  };

  static isDateInRange = (date: string, minDate: string, maxDate: string) =>
    moment(date).isBetween(minDate, maxDate, undefined, '[]');
}
